import { post as emailProspection } from '../content/blog/email-prospection-ugc';
import { post as trouverMarques } from '../content/blog/trouver-marques-ugc';
import { post as relancerMarque } from '../content/blog/relancer-marque-ugc';
import { post as tarifsUgc } from '../content/blog/tarifs-ugc';
import { post as tableauProspection } from '../content/blog/tableau-prospection-ugc';

const WORDS_PER_MINUTE = 220;

export interface BlogPost {
  slug: string;
  lang: 'fr' | 'en';
  title: string;
  description: string;
  date: string; // ISO YYYY-MM-DD
  author: string;
  tags: string[];
  ogImage?: string;
  readingTime: number; // minutes, computed automatically
  content: string; // HTML string
  ctaTitleKey?: string;
  ctaDescriptionKey?: string;
  ctaButtonKey?: string;
  ctaTrustKey?: string;
}

type BlogPostInput = Omit<BlogPost, 'readingTime'> & { readingTime?: number };

export type BlogPostDraft = BlogPostInput;

function stripHtml(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function countWords(html: string): number {
  const text = stripHtml(html);
  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
}

/** ~220 wpm for French prose with blockquotes and lists */
export function computeReadingTime(content: string, wpm = WORDS_PER_MINUTE): number {
  return Math.max(1, Math.round(countWords(content) / wpm));
}

function withReadingTime(post: BlogPostInput): BlogPost {
  return {
    ...post,
    readingTime: post.readingTime ?? computeReadingTime(post.content),
  };
}

const ALL_POSTS: BlogPost[] = [
  withReadingTime(tableauProspection),
  withReadingTime(tarifsUgc),
  withReadingTime(relancerMarque),
  withReadingTime(trouverMarques),
  withReadingTime(emailProspection),
];

export function getAllPosts(lang?: 'fr' | 'en'): BlogPost[] {
  const filtered = lang ? ALL_POSTS.filter((p) => p.lang === lang) : ALL_POSTS;
  const posts = filtered.length > 0 ? filtered : ALL_POSTS;
  return posts.sort((a, b) => b.date.localeCompare(a.date));
}

export function getPostBySlug(slug: string, lang?: 'fr' | 'en'): BlogPost | undefined {
  return ALL_POSTS.find((p) => p.slug === slug && (!lang || p.lang === lang));
}

export function getRelatedPosts(slug: string, lang?: 'fr' | 'en', limit = 3): BlogPost[] {
  return getAllPosts(lang)
    .filter((p) => p.slug !== slug)
    .slice(0, limit);
}

export function formatDate(dateStr: string, lang: 'fr' | 'en'): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}
